import { useEffect, useRef, useState } from 'react'
import { loadGoogleMaps } from '#lib/google-maps'

interface PlaceSuggestion {
	value: string
	label: string
	prediction: google.maps.places.PlacePrediction
}

/** Fewer letters give suggestions that cost a request and help nobody. */
const MIN_INPUT = 4
const WAIT_MS = 300

/**
 * The suggestions of Google Places (the Places API New, limited to Italy) for what is being typed:
 * one request after a pause, one search session from the first request to the details of the place
 * picked, and only the latest answer kept. `failed` is true while Google cannot be reached, so the
 * field using it can say so or stay a plain one. `primaryTypes` narrows the kind of place.
 */
function usePlaceSuggestions({
	apiKey,
	language,
	primaryTypes
}: {
	apiKey: string | undefined
	language: string
	primaryTypes?: string[]
}) {
	const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([])
	const [failed, setFailed] = useState(false)
	const token = useRef<google.maps.places.AutocompleteSessionToken>(undefined)
	const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
	const latest = useRef(0)

	useEffect(() => () => clearTimeout(timer.current), [])

	const ask = (text: string) => {
		clearTimeout(timer.current)
		const request = ++latest.current
		if (!apiKey || text.trim().length < MIN_INPUT) {
			setSuggestions([])
			return
		}
		timer.current = setTimeout(async () => {
			try {
				const places = await loadGoogleMaps(apiKey, 'places', language)
				token.current ??= new places.AutocompleteSessionToken()
				const { suggestions: found } =
					await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
						{
							input: text,
							includedRegionCodes: ['it'],
							includedPrimaryTypes: primaryTypes,
							language,
							region: 'it',
							sessionToken: token.current
						}
					)
				if (request === latest.current) {
					setFailed(false)
					setSuggestions(
						found.flatMap((item) =>
							item.placePrediction
								? [
										{
											value: item.placePrediction.placeId,
											label: item.placePrediction.text
												.text,
											prediction: item.placePrediction
										}
									]
								: []
						)
					)
				}
			} catch {
				if (request === latest.current) {
					setFailed(true)
					setSuggestions([])
				}
			}
		}, WAIT_MS)
	}

	/** A suggestion was picked: the ones on screen and the answers still on the way are dropped. */
	const close = () => {
		latest.current += 1
		setSuggestions([])
	}

	/** The details of the place are read: the next search starts a new session. */
	const endSession = () => {
		token.current = undefined
	}

	return { suggestions, failed, ask, close, endSession }
}

export { usePlaceSuggestions }
export type { PlaceSuggestion }
